import { useAuth } from '../contexts/AuthContext';

/**
 * Hook para obtener el rol efectivo del usuario con fallback chain
 * 
 * Este hook resuelve el rol del usuario desde múltiples fuentes en orden de prioridad:
 * 1. role (desde AuthContext state - más confiable después de carga inicial)
 * 2. userInfo?.user?.role_code (desde respuesta del backend - confiable durante relogin)
 * 3. userInfo?.user?.members?.[0]?.role (desde array de members - fallback)
 * 4. activeOrganization?.role_code (desde organización activa - último recurso)
 * 
 * @returns {Object} Objeto con effectiveRole, isSuperUser, isAdmin
 * 
 * @example
 * const { effectiveRole, isSuperUser, isAdmin } = useEffectiveRole();
 * if (isSuperUser) {
 *   // Mostrar funcionalidad de super usuario
 * }
 */
export const useEffectiveRole = () => {
  const { role, userInfo, activeOrganization } = useAuth();
  
  // Get effective role with fallback chain
  const effectiveRole = role || 
                        userInfo?.user?.role_code || 
                        userInfo?.user?.members?.[0]?.role || 
                        activeOrganization?.role_code || 
                        null;
  
  const isSuperUser = effectiveRole === 'super_user';
  const isAdmin = effectiveRole === 'admin';
  
  return {
    effectiveRole,
    isSuperUser,
    isAdmin,
    // Helper functions
    hasRole: (requiredRole) => {
      if (!requiredRole) return true;
      if (isSuperUser) return true;
      if (Array.isArray(requiredRole)) {
        return requiredRole.includes(effectiveRole);
      }
      if (isAdmin && ['admin', 'user'].includes(requiredRole)) return true;
      return requiredRole === 'user' && effectiveRole === 'user';
    },
    hasAnyRole: (...roles) => {
      if (isSuperUser) return true;
      return roles.some(r => effectiveRole === r);
    }
  };
};

export default useEffectiveRole;

